import { ChangeEvent } from 'react';
import { WaterlilyLogo } from './WaterlilyLogo';
import { cn } from '@/lib/utils';
import { LoadingState } from './LoadingState';
import { MdCheckCircle } from 'react-icons/md';
import { Button } from './ui/button';
import { Loader2 } from 'lucide-react';

function AuthBox(props: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn(`mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]`, props.className)}>
      <div className="rounded-[32px] bg-white px-6 py-12 shadow-2xl sm:px-12 ">
        {props.children}
      </div>
    </div>
  );
}

type AuthContainerProps = {
  children: React.ReactNode;
  title: string;
  outsideBoxChildren?: React.ReactNode;
  successState?: boolean;
  warningState?: boolean;
  isLoading?: boolean;
};

export function AuthContainer(props: AuthContainerProps) {
  return (
    <div className="bg-primary-200 flex min-h-screen flex-1 flex-col justify-center overflow-y-hidden py-12  sm:px-6 lg:px-8">
      <AuthBox
        className={cn(
          props.successState ? 'rounded-t-[32px] border-t-4 border-t-green-500' : '',
          props.warningState ? 'rounded-t-[32px] border-t-4 border-t-yellow-500' : '',
        )}
      >
        <AuthHeader
          title={props.title}
          className="mb-4"
        />
        {props.children}
        <LoadingState isLoading={props.isLoading} />
      </AuthBox>
      <div className="mt-4 flex items-center justify-center text-center text-sm text-gray-700">
        {props.outsideBoxChildren}
      </div>
    </div>
  );
}

type AuthHeaderProps = {
  title: string;
  className?: string;
};

function AuthHeader(props: AuthHeaderProps) {
  return (
    <div className="sm:mx-auto sm:w-full sm:max-w-md">
      <WaterlilyLogo className="mx-auto h-10 w-auto rounded-sm md:h-12" />
      {props.title && (
        <h2
          className={cn(
            ' text-center text-xl leading-9 tracking-tight text-gray-800',
            props.className,
          )}
        >
          {props.title}
        </h2>
      )}
    </div>
  );
}

interface AuthInputTextFieldProps {
  label: string | React.ReactNode;
  id: string;
  placeholder?: string;
  value?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  error?: boolean;
  type?: string;
  autoComplete?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  description?: string;
  endButton?: React.ReactNode;
}

export function AuthInputTextField({
  id,
  label,
  type,
  placeholder,
  required = true,
  disabled = false,
  value,
  onChange,
  onBlur,
  error,
  autoComplete,
  className,
  description,
  endButton,
}: AuthInputTextFieldProps) {
  return (
    <div className={cn('mt-2', className)}>
      <label
        htmlFor={id}
        className="mb-1 block text-sm font-medium leading-6 text-gray-900"
      >
        {label}
        {required && <span className="ml-1 text-red-700">*</span>}
      </label>

      {description && <p className="mb-1 text-sm text-gray-700">{description}</p>}

      <div className="relative">
        <input
          id={id}
          name={id}
          type={type ?? id}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete}
          className={`shadow-xs block w-full rounded-md border-0 bg-white px-2 py-1.5 text-gray-700 ring-1 ring-inset disabled:cursor-not-allowed disabled:bg-gray-100 ${
            error
              ? 'border-red-500 ring-red-500 focus:ring-red-500'
              : 'focus:ring-primary ring-gray-300'
          } placeholder:text-gray-500 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 ${endButton ? 'pr-10' : ''}`}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
        />
        {endButton && (
          <div className="absolute inset-y-0 right-0 flex items-center">{endButton}</div>
        )}
      </div>
    </div>
  );
}

type AuthInputDisabledFieldProps = {
  label: string;
  id: string;
  autoComplete?: string;
  value: string | undefined;
  className?: string;
};

export function AuthInputDisabledField({
  id,
  label,
  autoComplete,
  value,
  className,
}: AuthInputDisabledFieldProps) {
  return (
    <div className={cn('mt-2', className)}>
      <label
        htmlFor={id}
        className="my-1 block text-sm font-medium leading-6 text-gray-900"
      >
        {label}
      </label>
      <input
        id={id}
        name={id}
        type={id}
        autoComplete={autoComplete ?? 'off'}
        disabled
        className={`block w-full rounded-md border-0 bg-gray-100 px-2 py-1.5 text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-500 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 `}
        value={value}
      />
    </div>
  );
}

export function AuthSubmitButton(props: { title: string; disabled?: boolean }) {
  return (
    <Button
      type="submit"
      disabled={props.disabled}
      className="flex w-full"
    >
      {props.title}
    </Button>
  );
}

export function AuthPrimaryButton(props: {
  title: string;
  onClick: (() => void) | undefined;
  className?: string;
  type?: 'button' | 'submit';
  isLoading?: boolean;
}) {
  return (
    <Button
      onClick={props.onClick}
      className={cn(
        'flex w-full items-center justify-center gap-2 rounded-full',
        props.isLoading ? 'opacity-60' : '',
        props.className,
      )}
      type={props.type}
      disabled={props.isLoading}
    >
      {props.isLoading && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
      <span>{props.title}</span>
    </Button>
  );
}

export function AuthSuccessMessage(props: { title: string; message: string }) {
  return (
    <div className="flex max-w-xl flex-col items-center gap-4 px-4 py-8">
      <div className="flex items-center gap-3">
        <MdCheckCircle className="h-8 w-8 text-green-500" />
        <h2 className="text-xl font-medium text-gray-900"> {props.title}</h2>
      </div>
      <p className="text-center text-base text-gray-700 md:text-base">{props.message}</p>
    </div>
  );
}

interface AuthInputNumberFieldProps {
  label: string | React.ReactNode;
  id: string;
  placeholder?: string;
  value?: number | string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  error?: boolean;
  min?: number;
  max?: number;
  step?: number;
  autoComplete?: string;
  required?: boolean;
  className?: string;
  description?: string;
  unit?: string;
  width?: string;
  disabled?: boolean;
}

export function AuthInputNumberField({
  id,
  label,
  placeholder,
  required = true,
  value,
  onChange,
  onBlur,
  error,
  min,
  max,
  step,
  autoComplete,
  className,
  description,
  unit,
  width,
  disabled = false,
}: AuthInputNumberFieldProps) {
  return (
    <div className={cn('mt-2', className)}>
      <label
        htmlFor={id}
        className="mb-1 block text-sm font-medium leading-6 text-gray-900"
      >
        {label}
      </label>

      {description && <p className="mb-1 text-sm text-gray-700">{description}</p>}

      <div
        className="relative"
        style={width ? { width } : undefined}
      >
        <input
          id={id}
          name={id}
          type="number"
          required={required}
          autoComplete={autoComplete}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          className={cn(
            'block w-full rounded-md border-0 bg-white px-2 py-1.5 text-gray-700 shadow-sm ring-1 ring-inset disabled:cursor-not-allowed disabled:bg-gray-100',
            error
              ? 'border-red-500 ring-red-500 focus:ring-red-500'
              : 'focus:ring-primary ring-gray-300',
            unit ? 'pl-auto' : '',
            'placeholder:text-gray-500 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6',
          )}
          value={value === undefined || value === null ? '' : value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
        />
        {unit && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <span className="text-gray-700 sm:text-sm">{unit}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export function AuthSeparator(props: { text?: string; className?: string }) {
  return (
    <div className={cn('relative', props.className)}>
      <div className="absolute inset-0 flex items-center">
        <div className="w-full border-t border-gray-300" />
      </div>
      {props.text && (
        <div className="relative flex justify-center text-sm">
          <span className="bg-white px-2 text-gray-700">{props.text}</span>
        </div>
      )}
    </div>
  );
}
